import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bot, X, ArrowRight } from 'lucide-react';
import '@/styles/ios26-design-system.css';

/**
 * AssistantNudge Component
 * ========================
 * P0 POINT 5: Production-ready nudge popup for AI Assistant
 * 
 * Features:
 * - Appears after 8 seconds on app pages (not auth pages)
 * - Snoozes for 7 days after dismissal or click
 * - Doesn't appear if assistant is already open
 * - Supports dark/light mode via semantic tokens
 * - Accessible: role="dialog", aria-label, ESC key support
 * - Respects prefers-reduced-motion for accessibility
 * 
 * Integration:
 * - Dispatches 'ame:open-assistant' event when clicked
 * - Listens to 'ame:assistant-state' to know if assistant is open
 */

const SNOOZE_KEY = 'ame_assistant_nudge_snooze_until';
const SNOOZE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in ms
const SHOW_DELAY = 8000; // 8 seconds

export default function AssistantNudge({ assistantOpen = false }) {
  const [visible, setVisible] = useState(false);
  const mountedRef = useRef(false);
  
  // P0 POINT 5: Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Check if we should show the nudge
  const shouldShow = useCallback(() => {
    // SSR protection
    if (typeof window === 'undefined') return false;
    
    // Don't show on auth pages
    const path = window.location.pathname;
    if (path.includes('/auth') || path.includes('/login') || path.includes('/signup') || path === '/') {
      return false;
    }
    
    // Check snooze
    const snoozeUntil = localStorage.getItem(SNOOZE_KEY);
    if (snoozeUntil && Date.now() < parseInt(snoozeUntil, 10)) {
      return false;
    }
    
    return true;
  }, []);
  
  // Snooze the nudge for 7 days
  const snooze = useCallback(() => {
    if (typeof window !== 'undefined') {
      const until = Date.now() + SNOOZE_DURATION;
      localStorage.setItem(SNOOZE_KEY, until.toString());
    }
  }, []);
  
  // Handle close (X button or ESC key)
  const handleClose = useCallback((e) => {
    e?.stopPropagation();
    snooze();
    setVisible(false);
  }, [snooze]);
  
  // Handle click to open assistant
  const handleOpenAssistant = useCallback(() => {
    snooze();
    setVisible(false);
    
    // Dispatch custom event to open assistant
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ame:open-assistant'));
    }
  }, [snooze]);
  
  // P0 POINT 5: Handle Escape key for accessibility
  useEffect(() => {
    if (!visible) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [visible, handleClose]);
  
  // Show after delay (on mount)
  useEffect(() => {
    mountedRef.current = true;
    
    if (!shouldShow()) return;
    
    const timer = setTimeout(() => {
      if (shouldShow() && !assistantOpen && mountedRef.current) {
        setVisible(true);
      }
    }, SHOW_DELAY);
    
    return () => {
      clearTimeout(timer);
      mountedRef.current = false;
    };
  }, [shouldShow, assistantOpen]);
  
  // P0 POINT 5: Hide if assistant opens externally
  useEffect(() => {
    if (assistantOpen && visible) {
      snooze();
      // Use timeout to avoid synchronous setState in effect
      const timer = setTimeout(() => setVisible(false), 0);
      return () => clearTimeout(timer);
    }
  }, [assistantOpen, visible, snooze]);
  
  // Listen for assistant state changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleAssistantState = (e) => {
      if (e.detail?.open && visible) {
        snooze();
        setTimeout(() => setVisible(false), 0);
      }
    };
    
    window.addEventListener('ame:assistant-state', handleAssistantState);
    return () => window.removeEventListener('ame:assistant-state', handleAssistantState);
  }, [visible, snooze]);
  
  // Don't render if not visible
  if (!visible) return null;
  
  // P0 POINT 5: Animation classes respect prefers-reduced-motion
  const animationClasses = prefersReducedMotion 
    ? '' 
    : 'animate-in slide-in-from-bottom-4 fade-in duration-300';
  
  return (
    <div
      role="dialog"
      aria-label="Aide disponible"
      aria-live="polite"
      className={`fixed bottom-4 right-4 z-50 ${animationClasses}`}
      style={{ maxWidth: '320px' }}
      data-testid="assistant-nudge"
    >
      <div
        className="relative rounded-2xl p-4 cursor-pointer"
        style={{
          backgroundColor: 'hsl(var(--color-surface) / 0.85)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid hsl(var(--color-border) / 0.5)',
          boxShadow: '0 8px 32px hsl(var(--color-shadow) / 0.15), 0 4px 16px hsl(var(--color-shadow) / 0.1)',
          transition: prefersReducedMotion ? 'none' : 'transform 0.15s ease'
        }}
        onClick={handleOpenAssistant}
        onMouseEnter={(e) => {
          if (!prefersReducedMotion) {
            e.currentTarget.style.transform = 'scale(1.02)';
          }
        }}
        onMouseLeave={(e) => {
          if (!prefersReducedMotion) {
            e.currentTarget.style.transform = 'scale(1)';
          }
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 p-1.5 rounded-full transition-colors"
          style={{
            backgroundColor: 'hsl(var(--color-surface-2))',
            color: 'hsl(var(--color-text-muted))'
          }}
          aria-label="Fermer"
          data-testid="nudge-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
        
        {/* Content */}
        <div className="flex items-start gap-3 pr-6">
          {/* Icon */}
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, hsl(var(--color-primary)) 0%, hsl(270 60% 55%) 100%)'
            }}
          >
            <Bot className="w-5 h-5 text-white" />
          </div>
          
          {/* Text */}
          <div className="flex-1 min-w-0">
            <h4 
              className="font-semibold text-sm mb-0.5"
              style={{ color: 'hsl(var(--color-text))' }}
            >
              Assistant IA
            </h4>
            <p 
              className="text-xs leading-relaxed mb-2"
              style={{ color: 'hsl(var(--color-text-2))' }}
            >
              Besoin d&apos;aide ? Ouvrez l&apos;assistant à tout moment.
            </p>
            
            {/* CTA */}
            <span 
              className="inline-flex items-center gap-1 text-xs font-medium"
              style={{ color: 'hsl(var(--color-primary))' }}
            >
              Ouvrir
              <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
